import { useId } from 'react';
import { buildGraph, type BuildGraphOptions } from '@lib/nodeGraph';

interface NodeGraphProps extends BuildGraphOptions {
  className?: string;
  /**
   * 'slice' scales the graph to cover the container without distortion (hero);
   * 'none' stretches it to fill exactly, matching the original section usage.
   */
  fit?: 'slice' | 'none';
  /** Base opacity applied to unlit nodes. */
  nodeOpacity?: number;
  linkOpacityBase?: number;
  linkOpacityRange?: number;
  /** Node indices to render as brighter, glowing "live" nodes. */
  litNodes?: number[];
  /** Colour of the lit nodes; defaults to the ambient currentColor. */
  litColor?: string;
  /**
   * A smaller set of indices rendered brighter still than `litNodes` — larger,
   * fully opaque and with a wider glow. Takes precedence where the two overlap.
   */
  brightNodes?: number[];
  /** Colour of the bright nodes; defaults to `litColor`. */
  brightColor?: string;
}

/**
 * The brand's node-network motif rendered as a live SVG. Colour comes from the
 * ambient `currentColor` (set `text-teal` on the element or a parent), so it
 * adapts to context without extra props.
 */
export const NodeGraph = ({
  className,
  fit = 'slice',
  nodeOpacity = 0.5,
  linkOpacityBase = 0.09,
  linkOpacityRange = 0.24,
  litNodes = [],
  litColor = 'currentColor',
  brightNodes = [],
  brightColor,
  ...graphOptions
}: NodeGraphProps) => {
  const graph = buildGraph(graphOptions);
  const lit = new Set(litNodes);
  const bright = new Set(brightNodes);
  const glowId = useId();
  const brightGlowId = `${glowId}-bright`;

  return (
    <svg
      viewBox={`0 0 ${graph.W} ${graph.H}`}
      preserveAspectRatio={fit === 'slice' ? 'xMidYMid slice' : 'none'}
      className={className}
      aria-hidden="true"
    >
      <defs>
        <filter id={glowId} x="-200%" y="-200%" width="500%" height="500%">
          <feGaussianBlur stdDeviation="3" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        {/* Brightest nodes: just enough bloom to keep the disc from looking
            stamped-on, and the unblurred source merged last so the edge stays
            hard. Deliberately tighter than the standard lit glow — these read as
            intense because they're crisp, not because they're large. */}
        <filter id={brightGlowId} x="-200%" y="-200%" width="500%" height="500%">
          <feGaussianBlur stdDeviation="1.1" result="bloom" />
          <feMerge>
            <feMergeNode in="bloom" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <g stroke="currentColor">
        {graph.links.map(l => (
          <line
            key={`l-${l.x1}-${l.y1}-${l.x2}-${l.y2}`}
            x1={l.x1}
            y1={l.y1}
            x2={l.x2}
            y2={l.y2}
            strokeWidth={0.7}
            strokeOpacity={linkOpacityBase + l.o * linkOpacityRange}
          />
        ))}
      </g>
      <g fill="currentColor">
        {graph.nodes.map((n, i) => {
          // Inline `style` rather than the `fill` attribute throughout:
          // presentation attributes don't resolve var(), and the lit colours are
          // custom properties so they can be swapped per display capability.
          const key = `n-${n.x}-${n.y}`;
          if (bright.has(i)) {
            return (
              <circle
                key={key}
                cx={n.x}
                cy={n.y}
                r={n.r + 0.4}
                className="node-bright"
                style={{ fill: brightColor ?? litColor }}
                filter={`url(#${brightGlowId})`}
              />
            );
          }
          if (lit.has(i)) {
            return (
              <circle
                key={key}
                cx={n.x}
                cy={n.y}
                r={n.r + 1.2}
                style={{ fill: litColor }}
                fillOpacity={0.95}
                filter={`url(#${glowId})`}
              />
            );
          }
          return <circle key={key} cx={n.x} cy={n.y} r={n.r} fillOpacity={nodeOpacity} />;
        })}
      </g>
    </svg>
  );
};
